import React from 'react';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { OrbitPlottedPoint, isMeasured } from '../chartAdapters';
import { TOrbit } from '../i18n/TModules';
import { OrbitAnswerLabel, orbitTokens, orbitType } from '../styles';

export interface IOrbitDriverBarsProps {
  bars: OrbitPlottedPoint[];
  /**
   * The plotted metric's own name. div.driver-title `4952:8096` used to be filled with the constant
   * "Growth Drivers", which the PNG export then carried as the caption of a Lifecycle Stage
   * Distribution (PLA-9563 §6a): the export rasterises this box, so the constant was the only thing
   * naming the picture and it named the wrong thing.
   */
  title: string;
}

// div.driver-box `4952:8094`. §8 gives it radius 10 and a 1px #212D4B stroke; §7c's "r0, no stroke"
// prose is the stale reading (redesign-plan decision D3).
const Box = styled.div`
  padding: 22px 16px 14px;
  background: ${orbitTokens.surfaceAlt};
  border: 1px solid ${orbitTokens.border};
  border-radius: 10px;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-top: 12px;
  }
`;

const Row = styled.div`
  > * + * {
    margin-top: 6px;
  }
`;

const Labels = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${orbitTokens.textSecondary};
  ${orbitType.containerBody}
`;

const Value = styled.strong<{ $color: string }>`
  flex: 0 0 auto;
  margin-left: 12px;
  color: ${({ $color }) => $color};
  font-weight: 700;
`;

const Track = styled.div`
  height: 7px;
  background: ${orbitTokens.surface};
  border-radius: 4px;
`;

const Fill = styled.div<{ $color: string; $share: number }>`
  width: ${({ $share }) => $share}%;
  height: 100%;
  background: ${({ $color }) => $color};
  border-radius: 4px;
`;

/**
 * The fill is normalised against the largest reading, not against 100. The frame's three widths
 * (339.29 / 150.79 / 45.23 of a 377 track, against +42% / +18% / +5%) imply three different
 * divisors — 46.7, 45.0 and 41.7 — so they are hand-placed, not the output of one rule. The plan's
 * "within 1px of 90 / 40 / 12" is therefore unreachable; value-over-max is the rule this ships.
 */
const share = (value: number, largest: number): number =>
  largest === 0 ? 0 : Math.min(100, (Math.abs(value) / largest) * 100);

const OrbitDriverBars: React.FC<IOrbitDriverBarsProps> = ({ bars, title }) => {
  const { t } = useMultiLanguageTranslation();

  if (bars.length === 0) {
    return null;
  }

  const readings = bars.map(bar => bar.value).filter(isMeasured);
  const largest = readings.length === 0 ? 0 : Math.max(...readings.map(Math.abs));

  return (
    <Box data-testid="orbit-driver-bars">
      <OrbitAnswerLabel data-testid="orbit-driver-title">
        {title === '' ? t(`${TOrbit}.metrics.drivers`) : title}
      </OrbitAnswerLabel>
      {bars.map(bar => (
        <Row key={bar.label} data-testid="orbit-driver-row" data-measured={isMeasured(bar.value)}>
          <Labels>
            <span>{bar.label}</span>
            <Value $color={bar.color} data-testid="orbit-driver-value">
              {bar.display}
            </Value>
          </Labels>
          <Track>
            {/*
              A bucket the query could not measure draws NO track fill and reads the placeholder: a
              0%-wide bar is a measurement of nothing, which is not what this is (PLA-9563 §6c).
            */}
            {isMeasured(bar.value) ? (
              <Fill
                $color={bar.color}
                $share={share(bar.value, largest)}
                data-testid="orbit-driver-fill"
              />
            ) : null}
          </Track>
        </Row>
      ))}
    </Box>
  );
};

export default OrbitDriverBars;
